import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
    FiCheck,
    FiLayers,
    FiImage,
    FiAlertCircle,
    FiRefreshCw,
    FiArrowLeft,
    FiChevronRight,
} from 'react-icons/fi';
import {
    resolveDynamicCategoryImage,
    buildCategoryHierarchy,
} from '../../services/categoryService';
import './CategoryBrandExplorer.css';

const CATEGORY_BRAND_METADATA = {
    'air conditioner': {
        brands: ['Gree', 'Dawlance', 'PEL', 'Orient', 'TCL', 'Sabro'],
        badge: 'DC Inverters',
    },
    'cooler': {
        brands: ['Super Asia', 'Boss'],
        badge: 'Air Cooling',
    },
    'refrigerator': {
        brands: ['Dawlance', 'PEL', 'Waves', 'Orient'],
        badge: 'Food Care',
    },
    'freezer': {
        brands: ['Waves', 'PEL', 'Dawlance'],
        badge: 'Deep Chill',
    },
    'led': {
        brands: ['TCL', 'Samsung', 'Multynet', 'Eco Star', 'Konvo'],
        badge: 'Smart 4K',
    },
    'tv': {
        brands: ['TCL', 'Samsung', 'Multynet', 'Eco Star', 'Konvo'],
        badge: 'Smart 4K',
    },
    'washing': {
        brands: ['Super Asia', 'Boss', 'Dawlance', 'PEL'],
        badge: 'Automatic',
    },
    'kitchen': {
        brands: ['Care', 'Dawlance', 'PEL'],
        badge: 'Hoods & Hobs',
    },
    'gas': {
        brands: ['Care', 'Super Asia', 'Boss'],
        badge: 'Kitchen & Gas',
    },
    'geyser': {
        brands: ['Super Asia', 'Boss', 'Care'],
        badge: 'Instant & Gas',
    },
    'heater': {
        brands: ['Super Asia', 'Boss', 'Care'],
        badge: 'Instant & Gas',
    },
    'mobile': {
        brands: ['Samsung', 'United'],
        badge: 'Smartphones',
    },
    'domestic': {
        brands: ['Super Asia', 'Boss', 'Care'],
        badge: 'Small Appliances',
    },
};

function getCategoryMeta(cat) {
    const apiBrands = Array.isArray(cat?.brands)
        ? cat.brands.map((b) => String(b || '').trim()).filter(Boolean)
        : [];
    const apiBadge = typeof cat?.badge === 'string' ? cat.badge.trim() : '';

    const lower = (cat?.name || '').toLowerCase();
    for (const [key, meta] of Object.entries(CATEGORY_BRAND_METADATA)) {
        if (lower.includes(key)) {
            return {
                brands: apiBrands.length > 0 ? apiBrands : meta.brands,
                badge: apiBadge || meta.badge,
            };
        }
    }

    return {
        brands: apiBrands.length > 0 ? apiBrands : ['Dawlance', 'PEL', 'Super Asia', 'Boss', 'Care'],
        badge: apiBadge || 'Official Brand',
    };
}

const ExplorerCategoryCard = ({ cat, isSelected, onSelect }) => {
    const imgRef = useRef(null);
    const [imgLoaded, setImgLoaded] = useState(false);
    const [imgFailed, setImgFailed] = useState(false);

    useEffect(() => {
        setImgLoaded(false);
        setImgFailed(false);

        const el = imgRef.current;
        if (cat.image && el && el.complete) {
            if (el.naturalWidth > 0) {
                setImgLoaded(true);
            } else {
                setImgFailed(true);
            }
        }
    }, [cat.image]);

    const hasValidImage = Boolean(cat.image) && !imgFailed;
    const isCoverFit = cat.imageFit === 'cover';
    const hasSubcategories = cat.subcategoryCount > 0;

    return (
        <div
            className={`cbe-card ${isSelected ? 'active' : ''}`}
            onClick={() => onSelect(cat.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(cat.id);
                }
            }}
            title={
                hasSubcategories
                    ? `Explore ${cat.name} subcategories`
                    : `Browse ${cat.name}`
            }
        >
            <div className="cbe-card-media">
                {hasValidImage ? (
                    <>
                        {!imgLoaded && <div className="cbe-card-shimmer" aria-hidden="true" />}
                        <img
                            ref={imgRef}
                            src={cat.image}
                            alt={cat.imageAlt || cat.name}
                            className={`cbe-card-img${isCoverFit ? ' cbe-card-img--cover' : ''}${
                                imgLoaded ? ' is-loaded' : ' is-loading'
                            }`}
                            loading="lazy"
                            decoding="async"
                            onLoad={() => {
                                setImgLoaded(true);
                                setImgFailed(false);
                            }}
                            onError={() => {
                                setImgFailed(true);
                                setImgLoaded(false);
                            }}
                        />
                    </>
                ) : (
                    <div
                        className="cbe-card-empty-media"
                        role="img"
                        aria-label={
                            imgFailed
                                ? `${cat.name} image unavailable`
                                : `${cat.name} category`
                        }
                    >
                        <FiImage className="cbe-card-empty-icon" aria-hidden="true" />
                        <span className="cbe-card-empty-text">
                            {imgFailed ? 'Image unavailable' : 'No image'}
                        </span>
                    </div>
                )}

                {cat.badge && <span className="cbe-card-badge">{cat.badge}</span>}
                {isSelected && (
                    <span className="cbe-card-check" aria-label="Selected">
                        <FiCheck />
                    </span>
                )}
            </div>

            <div className="cbe-card-body">
                <div className="cbe-card-title-row">
                    <h3 className="cbe-card-name">{cat.name}</h3>
                    {hasSubcategories && (
                        <FiChevronRight className="cbe-card-drill-icon" aria-hidden="true" />
                    )}
                </div>
                {hasSubcategories ? (
                    <p className="cbe-card-brands cbe-card-subcount">
                        {cat.subcategoryCount}{' '}
                        {cat.subcategoryCount === 1 ? 'Subcategory' : 'Subcategories'}
                    </p>
                ) : (
                    <p className="cbe-card-brands">
                        {cat.brands.slice(0, 3).join(' • ')}
                        {cat.brands.length > 3 ? '...' : ''}
                    </p>
                )}
            </div>
        </div>
    );
};

export const CategoryBrandExplorer = ({
    categories = [],
    hierarchy: hierarchyProp = null,
    activeParentId = null,
    loading = false,
    error = '',
    onRetry,
    selectedCat,
    selectedBrand,
    onSelectCategory,
    onNavigateParent,
    onSelectBrand,
}) => {
    const isAll = !selectedCat || selectedCat === 'all';

    const fallbackHierarchy = useMemo(
        () => buildCategoryHierarchy(categories),
        [categories]
    );
    const resolvedHierarchy = hierarchyProp || fallbackHierarchy;

    const activeParent = activeParentId
        ? resolvedHierarchy.getCategoryById(activeParentId)
        : null;

    const ancestors = useMemo(
        () => (activeParent ? resolvedHierarchy.getAncestors(activeParent._id) : []),
        [activeParent, resolvedHierarchy]
    );

    const visibleRawCategories = useMemo(() => {
        if (activeParent) {
            return resolvedHierarchy.getSubcategories(activeParent._id);
        }
        return resolvedHierarchy.parentCategories;
    }, [activeParent, resolvedHierarchy]);

    // Map live categories strictly using dynamic API images (never hardcoded fallback images)
    const explorerCategories = useMemo(() => {
        return (visibleRawCategories || []).map((cat) => {
            const meta = getCategoryMeta(cat);
            const image = resolveDynamicCategoryImage(cat);
            const subcategoryCount = resolvedHierarchy.getSubcategories(cat._id).length;
            return {
                id: cat._id,
                _id: cat._id,
                name: cat.name,
                parent_id: cat.parent_id || null,
                subcategoryCount,
                image,
                imageFit: cat.image_fit === 'cover' ? 'cover' : 'contain',
                imageAlt: cat.image_alt || cat.name,
                brands: meta.brands,
                badge: meta.badge,
            };
        });
    }, [visibleRawCategories, resolvedHierarchy]);

    const activeCategoryObj = useMemo(() => {
        if (isAll) return null;
        const rawSelected = resolvedHierarchy.getCategoryById(selectedCat);
        if (!rawSelected) return null;
        const meta = getCategoryMeta(rawSelected);
        return {
            id: rawSelected._id,
            _id: rawSelected._id,
            name: rawSelected.name,
            brands: meta.brands,
        };
    }, [isAll, resolvedHierarchy, selectedCat]);

    const handleCategoryClick = (catId) => {
        if (onSelectCategory) {
            onSelectCategory(catId);
        }
        // Reset brand filter to all when changing category so all models show
        if (onSelectBrand) {
            onSelectBrand('all');
        }
    };

    const handleBackClick = () => {
        const upParentId = activeParent?.parent_id ? String(activeParent.parent_id) : null;
        if (onNavigateParent) {
            onNavigateParent(upParentId);
        } else if (onSelectCategory) {
            onSelectCategory('all');
        }
    };

    const backButtonLabel = activeParent?.parent_id
        ? resolvedHierarchy.getCategoryById(activeParent.parent_id)?.name || 'Main Categories'
        : 'Main Categories';

    return (
        <section className="cbe-container" aria-label="Product categories and brand filters">
            {/* ── Sleek Section Header ── */}
            <div className="cbe-header">
                <div className="cbe-header-left">
                    <span className="cbe-eyebrow">
                        {activeCategoryObj
                            ? 'SELECTED CATEGORY'
                            : activeParent
                              ? 'SELECT SUBCATEGORY'
                              : 'BROWSE BY CATEGORY'}
                    </span>
                    <h2 className="cbe-title">
                        {activeCategoryObj
                            ? activeCategoryObj.name
                            : activeParent
                              ? activeParent.name
                              : 'Select Appliance Category'}
                    </h2>
                </div>

                <button
                    type="button"
                    className={`cbe-all-btn ${isAll && !activeParent ? 'active' : ''}`}
                    onClick={() => handleCategoryClick('all')}
                >
                    <FiLayers />
                    <span>All Products</span>
                </button>
            </div>

            {loading ? (
                <div className="cbe-grid" aria-label="Loading categories">
                    {[1, 2, 3, 4].map((n) => (
                        <div key={n} className="cbe-card-skeleton" />
                    ))}
                </div>
            ) : error ? (
                <div className="cbe-state-box">
                    <FiAlertCircle className="cbe-state-icon" />
                    <p>{error}</p>
                    {onRetry && (
                        <button type="button" className="cbe-retry-btn" onClick={onRetry}>
                            <FiRefreshCw /> Retry
                        </button>
                    )}
                </div>
            ) : explorerCategories.length === 0 ? (
                <div className="cbe-state-box">
                    <FiAlertCircle className="cbe-state-icon" />
                    <p>No categories currently available.</p>
                    {activeParent && (
                        <button
                            type="button"
                            className="cbe-retry-btn"
                            onClick={() => onNavigateParent && onNavigateParent(null)}
                        >
                            <FiArrowLeft /> Back to Main Categories
                        </button>
                    )}
                </div>
            ) : (
                <>
                    {/* ── Subcategory Back & Breadcrumb Bar ── */}
                    {activeParent && (
                        <div className="cbe-hierarchy-bar">
                            <button
                                type="button"
                                className="cbe-back-btn"
                                onClick={handleBackClick}
                            >
                                <FiArrowLeft aria-hidden="true" />
                                <span>Back to {backButtonLabel}</span>
                            </button>

                            <nav className="cbe-breadcrumb" aria-label="Category hierarchy">
                                <button
                                    type="button"
                                    className="cbe-crumb-btn"
                                    onClick={() =>
                                        onNavigateParent
                                            ? onNavigateParent(null)
                                            : handleCategoryClick('all')
                                    }
                                >
                                    Main Categories
                                </button>
                                {ancestors.map((anc) => (
                                    <React.Fragment key={anc._id}>
                                        <FiChevronRight
                                            className="cbe-crumb-sep"
                                            aria-hidden="true"
                                        />
                                        <button
                                            type="button"
                                            className="cbe-crumb-btn"
                                            onClick={() =>
                                                onNavigateParent &&
                                                onNavigateParent(String(anc._id))
                                            }
                                        >
                                            {anc.name}
                                        </button>
                                    </React.Fragment>
                                ))}
                                <FiChevronRight className="cbe-crumb-sep" aria-hidden="true" />
                                <span
                                    className={
                                        activeCategoryObj ? 'cbe-crumb-parent' : 'cbe-crumb-current'
                                    }
                                >
                                    {activeParent.name}
                                </span>
                                {activeCategoryObj &&
                                    String(activeCategoryObj.id) !== String(activeParent._id) && (
                                        <>
                                            <FiChevronRight
                                                className="cbe-crumb-sep"
                                                aria-hidden="true"
                                            />
                                            <span className="cbe-crumb-current">
                                                {activeCategoryObj.name}
                                            </span>
                                        </>
                                    )}
                            </nav>
                        </div>
                    )}

                    {/* ── Dynamic Category Cards Grid from Real API ── */}
                    <div className="cbe-grid">
                        {explorerCategories.map((cat) => (
                            <ExplorerCategoryCard
                                key={cat.id}
                                cat={cat}
                                isSelected={String(selectedCat) === String(cat.id)}
                                onSelect={handleCategoryClick}
                            />
                        ))}
                    </div>

                    {/* ── Contextual Brand Filter Row (Shows when a category/subcategory is selected) ── */}
                    {activeCategoryObj && (
                        <div className="cbe-brands-panel">
                            <div className="cbe-brands-panel-header">
                                <span className="cbe-brands-panel-label">
                                    Authorized Brands in <strong>{activeCategoryObj.name}</strong>:
                                </span>
                            </div>

                            <div className="cbe-brands-chips-wrap">
                                <button
                                    type="button"
                                    className={`cbe-brand-chip ${!selectedBrand || selectedBrand === 'all' ? 'active' : ''}`}
                                    onClick={() => onSelectBrand && onSelectBrand('all')}
                                >
                                    All {activeCategoryObj.name}
                                </button>
                                {activeCategoryObj.brands.map((brand) => {
                                    const isBrandActive =
                                        selectedBrand &&
                                        selectedBrand.toLowerCase() === brand.toLowerCase();
                                    return (
                                        <button
                                            key={brand}
                                            type="button"
                                            className={`cbe-brand-chip ${isBrandActive ? 'active' : ''}`}
                                            onClick={() => onSelectBrand && onSelectBrand(brand)}
                                        >
                                            {brand}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </>
            )}
        </section>
    );
};

export default CategoryBrandExplorer;

